import { isDevMode, PLATFORM_ID, inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { LocalStorageService } from '../storage/local-storage.service';
import { StateService } from '../storage/state.service';
import {
  ApiUrls,
  notificationSystemUrls,
  userAccountUrls
} from '../../enum/routes';
import { StoreDetailService } from '../resolver/store-detail.service';
import { TranslateService } from '@ngx-translate/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { _StoreApiKeys } from 'store.env';
import { ChannelSourceService } from '../channel-source/channel-source.service';
import { LocalStorageVariables } from '../../enum/localstorage-variables';

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  const localStorageService = inject(LocalStorageService);
  const stateService = inject(StateService);
  const storeResolver = inject(StoreDetailService);
  const translateService = inject(TranslateService);
  const channelSourceService = inject(ChannelSourceService);
  const platformId = inject(PLATFORM_ID);
  const isServer = isPlatformServer(platformId);

  // Skip loading state during SSR
  if (!isServer) {
    
    if (
      !req.url.includes(ApiUrls.searchProductByName) &&
      !req.url.includes(ApiUrls.clubSearch) &&
      !req.url.includes(ApiUrls.trendingQueries) &&
      !req.url.includes(ApiUrls.cryptoGatewayInternal) &&
      !req.url.includes(ApiUrls.storeCountries) &&
      !req.url.includes(ApiUrls.storeDetail) &&
      !req.url.includes(ApiUrls.storeGroups) &&
      !req.url.includes(ApiUrls.FAQ) &&
      !req.url.includes(ApiUrls.exchangeDetail)

    ) {
      stateService.startLoading();
    }
  }

  // Get channel source header - safe for SSR (returns empty object if server)
  const channelHeaders = toSafeHeaders(
    isServer && !channelSourceService.channelSource
      ? {}
      : channelSourceService.channelSourceHeader(req.url)
  );

  let modifiedReq = req.clone({
    setHeaders: channelHeaders
  });

  let accessToken =
    stateService.accessToken() ??
    localStorageService.getItem(LocalStorageVariables.access_token);
  
  const currentLang = isServer ? 'en' : translateService.getCurrentLang();
  
  if (checkAccessType(modifiedReq, accessToken, platformId, localStorageService)) {
    modifiedReq = modifiedReq.clone({
      setHeaders: toSafeHeaders({
        ...channelHeaders,
        'API-KEY': getApiKey(platformId, localStorageService),
        'Accept-Language': `${currentLang}`
      })
    });
  } else if (isUserAccountUrl(modifiedReq.url)) {
    modifiedReq = modifiedReq.clone({
      setHeaders: toSafeHeaders({
        ...channelHeaders,
        'project-api-key':
          storeResolver?.permissionInit?.client_id ??
          localStorageService.getItem(LocalStorageVariables.client_id),
        'Accept-Language': `${currentLang}`,
        ...(accessToken && {
          Authorization: `Token ${accessToken}`
        })
      })
    });
  } else if (accessToken) {
    modifiedReq = modifiedReq.clone({
      setHeaders: toSafeHeaders({
        ...channelHeaders,
        Token: `${accessToken}`,
        'API-KEY': getApiKey(platformId, localStorageService),
        'Accept-Language': `${currentLang}`
      })
    });
  }

  return next(modifiedReq).pipe(
    finalize(() => {
      // Only end loading in browser
      if (!isServer) {
        stateService.endLoading();
      }
    })
  );
};

function checkAccessType(
  req: HttpRequest<any>,
  accessToken: string | null,
  platformId: Object,
  localStorageService: LocalStorageService
): boolean {
  if (
    (((req.url.includes(ApiUrls.products) && !accessToken) ||

      req.url.includes(ApiUrls.productComments) ||
      req.url.includes(ApiUrls.googleLogin) ||
      req.url.includes(ApiUrls.appleLogin) ||
      req.url.includes(ApiUrls.smsLogin) ||
      req.url.includes(ApiUrls.smsVerification) ||
      req.url.includes(ApiUrls.exchangeDetail) ||
      req.url.includes(ApiUrls.gateway) ||
      req.url.includes(ApiUrls.gatewayGroup) ||
      req.url.includes(ApiUrls.category) ||
      (req.url.includes(ApiUrls.loginUrl) && !isRefreshToken(req.url)) ||
      req.url.includes(ApiUrls.signUp) ||
      req.url.includes(ApiUrls.storeCountries) ||
      req.url.includes(ApiUrls.countryCode) ||
      req.url.includes(ApiUrls.storeGroups) ||
      req.url.includes(ApiUrls.clubSearch) ||
      (req.url.includes(ApiUrls.searchProductByName) && !accessToken) ||
      req.url.includes(ApiUrls.storeDetail) ||
      req.url.includes(ApiUrls.FAQ) ||
      req.url.includes(ApiUrls.cryptoCurrency) ||
      req.url.includes(ApiUrls.trendingQueries) ||
      (req.url.includes(ApiUrls.home) && !accessToken) ||
      (req.url.includes(ApiUrls.homes) && !accessToken)) &&
        !req.url.includes(ApiUrls.favorite) &&
        !req.url.includes('delete-favorite') &&
        !req.url.includes('add-favorite')) ||
    req.url.includes(ApiUrls.checkStockAvailability) ||
    req.url.includes(ApiUrls.logProductSearch) ||
    req.url.includes(notificationSystemUrls.scenarios) ||
    req.url.includes(notificationSystemUrls.unsubscribeScenarios) || 

    req.url.includes('loyalty-content')  ||
    req.url.includes('related-products')
  ) {
    return true;
  } else {
    return false;
  }
}

function getApiKey(platformId: Object, localStorageService: LocalStorageService): string {
  let storeApiKey: string | undefined;
  if (isPlatformBrowser(platformId)) {
    storeApiKey =
      localStorageService.getItem(LocalStorageVariables.store_key) ||
      (isDevMode()
        ? _StoreApiKeys[1].value.apikey
        : _StoreApiKeys[0].value.apikey);
  }
  if (isPlatformServer(platformId)) {
    storeApiKey = 'c7546d31-870c-4a1b-801f-b0423d2004c3';
  }
  return storeApiKey || '';
}

function isUserAccountUrl(url: string): boolean {
  return Object.keys(userAccountUrls).some((key) =>
    url.includes(userAccountUrls[key])
  );
}

function isRefreshToken(reqUrl: string): boolean {
  return reqUrl.includes(userAccountUrls.refreshToken);
}

/** Angular HttpHeaders throws if any value is null/undefined. */
function toSafeHeaders(
  headers: Record<string, string | null | undefined>
): Record<string, string> {
  const safe: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (value != null) {
      safe[key] = value;
    }
  }
  return safe;
}
